import Cookies from 'js-cookie';
import { v4 as uuid } from 'uuid';
import { ElementShared } from '../eventing/constants';
import { TagularEventingRequiredProps } from '../eventing/types/main';

declare global {
  // eslint-disable-next-line @typescript-eslint/consistent-type-definitions
  interface Window {
    /**
     * Cohesion pageType for the current page. Set this to have the webContext
     * autofilled by tagular along with the page type.
     */
    chsn_pageType?: string;
  }
}

class AssertionError extends Error {
  constructor(message: string) {
    super(message);
    this.name = 'AssertionError';
    Object.setPrototypeOf(this, AssertionError.prototype);
  }
}

function isServerSideCode(): boolean {
  return typeof window === 'undefined';
}

function shouldDebugTagular(): boolean {
  // Stolen from: https://github.com/RedVentures/cohesion-client-js/blob/main/src/libs/tagular/debug.js#L3
  if (isServerSideCode()) {
    return false;
  }

  function getSessionStorage(name: string) {
    try {
      return sessionStorage.getItem(name);
    } catch {
      return undefined;
    }
  }

  const TAGULAR_DEBUG_KEY = 'tagularDebug';
  const url = new URL(window.location.href);
  const param = url.searchParams.get(TAGULAR_DEBUG_KEY);
  const session = getSessionStorage(TAGULAR_DEBUG_KEY);

  // eslint-disable-next-line @typescript-eslint/prefer-nullish-coalescing
  return (param && param === 'true') || (!param && session === 'true');
}

/**
 * A Tagular sensitive Assertion Mechanism, if you have tagularDebug=true in your SessionStorage or AddressBar we will
 * throw an exception so you cant miss it.
 *
 * This has the same functionality of `console.assert`
 *
 * @see console.assert
 * @param condition
 * @param data
 * @throws AssertionError
 */
export function assert(condition: boolean, ...data: string[]): boolean {
  const shouldThrow = shouldDebugTagular();

  // eslint-disable-next-line no-console
  const log = console.assert;

  const buildMessage = (dataObj: string[]): string => {
    if (typeof dataObj[0] === 'string') {
      const [template, ...substitutions] = dataObj;
      return template.replace(/%[sdifoO]/g, () => substitutions.shift() ?? '');
    }
    return dataObj.map((item) => JSON.stringify(item)).join(' ');
  };

  if (!condition) {
    if (shouldThrow) {
      const message = data.length > 0 ? buildMessage(data) : 'Assertion failed';
      throw new AssertionError(message);
    } else {
      log(condition, ...data);
    }
  }

  return condition;
}

/**
 * Fill the Window page tracking variables to permit Tagular/Cohesion to full in
 * the webContext automatically.
 * @param pageType The page type to be submitted along with the event.
 * @param override Should force the change
 */
export function pageTrackingObjectUpdate(
  pageType: string | undefined,
  override = false,
): void {
  if (!override) {
    assert(
      window.chsn_pageType !== undefined && window.chsn_pageType !== pageType,
      'You are not permitted to override the page type.',
    );
    return;
  }
  window.chsn_pageType = pageType;
}

/**
 * Get the current page type.
 */
export function getPageType(): string | undefined {
  return window.chsn_pageType;
}

/**
 * Determine if Tagular should event on the core page invoking this element.
 */
export function isTagularEnabledForPage(): boolean {
  return getPageType() !== undefined;
}

/**
 * Determines if we have what we need in TagularEventingRequiredProps and asserts
 * that those required values are set.
 *
 * @param props A dict of properties that may or may not contain what we need for eventing
 * @param checkWindowConfig Should we check the window to see if were setup (default: true)
 */
export function isTagularEnabledForElement(
  props: TagularEventingRequiredProps,
  checkWindowConfig = true,
): props is {
  html_id: string;
  location: string;
  [key: string]: any; // eslint-disable-line @typescript-eslint/no-explicit-any
} {
  return (
    (checkWindowConfig ? isTagularEnabledForPage() : true) &&
    typeof props.html_id === 'string' &&
    typeof props.location === 'string'
  );
}

/**
 * Convert a ElementShared object to a cloned TagularWebElement
 * @param shared ElementShared instance
 */
export function convertElementSharedToWebElement(
  shared: ElementShared,
): ElementShared['webElement'] {
  return { ...shared.webElement };
}

/**
 * Make Near Slugs from Plain Strings for ease of eventing.
 *
 * @example
 *   "Computer Science" => "computer-science"
 *   "Humanities & Arts" => "humanities-&-arts"
 *   "Someone added a space " => "someone-added-a-space"
 *
 * @param x Input String
 */
export function hyphenateForTagular(x: string): string {
  return x
    .trim()
    .toLowerCase()
    .replace(/[^\w&]/g, '-');
}

/**
 * Derive Tagular product variant/category from a course marketing URL path,
 * matching www.edx.org `getVariantCategoryFromUrl` (learn/exec-ed/... → courses, exec-ed).
 */
export function getVariantCategoryFromUrl(url: string): {
  variant: string;
  category: string;
} {
  const normalizedUrl = url.trim();

  if (!normalizedUrl) {
    return { variant: '', category: '' };
  }

  let pathname = normalizedUrl;
  try {
    const origin =
      typeof window !== 'undefined'
        ? window.location.origin
        : 'https://www.edx.org';
    pathname = new URL(normalizedUrl, origin).pathname;
  } catch {
    // keep pathname as url
  }

  let urlWithoutCertificates = pathname.replace('/certificates', '');
  if (urlWithoutCertificates.startsWith('/')) {
    urlWithoutCertificates = urlWithoutCertificates.slice(1);
  }

  const urlFolders = urlWithoutCertificates.split('/');
  let variant = hyphenateForTagular(urlFolders[0] ?? '');
  let category = '';
  if (urlFolders.length >= 2) {
    category = hyphenateForTagular(urlFolders[1] ?? '') || '';
  }

  const replacements: Record<string, string> = {
    'boot-camps': 'bootcamps',
    learn: 'courses',
  };
  Object.entries(replacements).forEach(([key, value]) => {
    variant = variant.replace(key, value);
  });

  return { variant, category };
}

/**
 * Fetch or Create a Tagular CorrelationID. This also refreshes the cookie's expiry.
 */
export function getCorrelationID(): string {
  const COOKIE_NAME = 'tglr_correlation_id';
  const PARAM_NAME = 'correlationId';

  function getDomain() {
    const { hostname } = window.location;
    const parts = hostname.split('.');
    return parts.length > 2 ? parts.slice(-2).join('.') : hostname;
  }

  function getQueryParameter(name: string): string | null {
    const params = new URLSearchParams(window.location.search);

    return params.get(name);
  }

  let paramId = getQueryParameter(PARAM_NAME) ?? Cookies.get(COOKIE_NAME);

  // If the tagular correlation ID cookie was set before we added the change to
  // specify the domain, it was automatically added to the current domain.
  // Always delete the cookie with the current domain
  Cookies.remove(COOKIE_NAME, { domain: window.location.hostname, path: '/' });

  paramId ??= uuid();

  Cookies.set(COOKIE_NAME, paramId, {
    expires: 1 / 48 /* 30 mins */,
    domain: `.${getDomain()}`,
    path: '/',
  });

  return paramId;
}
